import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import Barcode from 'react-barcode';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Dialog } from 'src/components/Dialog';
import { Icon } from 'src/components/Icon';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { modalAppointmentCheckIn } from 'src/models/appointment/appointment';
import { getUserDisplayName } from 'src/models/user/utils';
import { CheckInBlockerDialog } from 'src/shared/CheckinBlocker/CheckInBlockerDialog';
import { useCheckInBlocker } from 'src/shared/CheckinBlocker/useCheckInBlocker';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';

import type { User } from 'src/models/user/_types';

interface AppointmentCheckinModalProps {
  open: boolean;
  onHandleClose?: () => void;
}

const AppointmentCheckinModal = ({
  open,
  onHandleClose,
}: AppointmentCheckinModalProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appointment.checkinModal',
  });

  const { t: tCheckinBlocker } = useTranslation('translation', { keyPrefix: 'checkinBlocker' });
  const { user, session } = useSession();
  const queryClient = useQueryClient();
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkInBlockerDialogProps, checkBlockers] = useCheckInBlocker({
    title: tCheckinBlocker('actionRequired'),
  });

  const updateAppointmentCheckIn = useMutation({
    mutationFn: (user: User) => {
      return modalAppointmentCheckIn(user);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      setCheckedIn(true);
      toast.success(t('successMessage'));
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('errorMessage')));
    },
  });

  const handleUserCheckIn = async () => {
    const blockers = await checkBlockers({ userIds: [user.id] });
    if (blockers.length > 0) return;
    updateAppointmentCheckIn.mutate({ ...user });
  };

  const useBarcode = session.requireBarcodesForCheckin && user.barcodeHash;

  const buttonProps = checkedIn
    ? {
        children: (
          <Stack spacing={1} direction="row" alignItems="center">
            <Icon icon={faCheckCircle} />
            <Typography>{t('checkedIn')}</Typography>
          </Stack>
        ),
      }
    : { label: t('checkInNow'), onClick: handleUserCheckIn };

  return (
    <>
      <style>
        {`
          .barcodeId {
            max-width: 100%;
            width: auto !important;
          }

          .barcodeId svg {
            max-width: 100%;
            height: auto;
          }
        `}
      </style>
      <Dialog
        open={open}
        onClose={onHandleClose}
        title={t('title')}
        titleVariant="h6"
        spacing={0.5}
        actions={
          useBarcode
            ? null
            : [
                {
                  ...buttonProps,
                  variant: 'prominent',
                  disabled: checkedIn,
                  fullWidth: true,
                },
              ]
        }
      >
        <Stack
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          spacing={3}
          sx={{ mb: 5 }}
        >
          <Avatar src={user?.profileImage} size="xlarge" />
          <Typography variant="body1">{getUserDisplayName(user)}</Typography>

          {useBarcode && user.barcodeHash && (
            <Box className="barcodeId">
              <Barcode
                height={36}
                fontSize={14}
                displayValue={false}
                value={user.barcodeHash}
              />
            </Box>
          )}
          {useBarcode && (
            <Typography variant="body2" color="secondary">
              {user.barcodeHash}
            </Typography>
          )}
        </Stack>
      </Dialog>
      <CheckInBlockerDialog {...checkInBlockerDialogProps} />
    </>
  );
};

export default AppointmentCheckinModal;
